import { DemographicsUserBasedNoDataEvent } from "app/pages/website-analysis/demographics/services/DemographicsUserBasedWidgets";
import type { IAgeDistribution } from "app/pages/website-analysis/demographics/types";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import DurationService from "app/services/DurationService";
import { DefaultFetchService } from "app/services/fetchService";
import { DEFAULT_WEBSITES_DELIMITER } from "app/services/sitesService/consts";
import { isIncludeSubDomains } from "app/UtilitiesAndConstants/UtilityFunctions/common";

export interface IAgeRatios extends Omit<IAgeDistribution, "Age65Plus"> {
    Age65: number;
}

interface IDistributionData {
    FemalesRatio: number;
    AgeRatios: IAgeRatios;
}
type IDomain = string;
export type IRawData = Record<IDomain, IDistributionData>;

const ENDPOINT = "widgetApi/DemographicSegments/Sectors/Table";
const EXCEL_ENDPOINT = "widgetApi/DemographicSegments/Sectors/Excel";

const EMPTY_VALUE = {
    AgeRatios: {
        Age18To24: undefined,
        Age25To34: undefined,
        Age35To44: undefined,
        Age45To54: undefined,
        Age55To64: undefined,
        Age65Plus: undefined,
    },
    FemalesRatio: undefined,
};

const getQueryParams = () => {
    const { isWWW, country, duration, webSource, key } = proNavigator.getParams();
    const includeSubDomains = isIncludeSubDomains(isWWW);
    const { from, to } = DurationService.getDurationData(duration).forAPI;
    const queryParams = { from, to, country, webSource, includeSubDomains, keys: key };
    return queryParams;
};

const isNoDistributionData = (distributionData: IDistributionData): boolean => {
    const { FemalesRatio, AgeRatios } = distributionData;
    const isNoGenderData = Number.isNaN(FemalesRatio) || !FemalesRatio;
    const isNoAgeData = Object.values(AgeRatios).every((bucketData) => Number.isNaN(bucketData) || !bucketData);
    const result = isNoAgeData && isNoGenderData;
    return result;
};

const isNoData = (rawData: IRawData) => {
    if (!rawData) {
        return true;
    }
    const result = Object.values(rawData).every(isNoDistributionData);
    return result;
};

export const fetchData = (): Promise<IRawData> => {
    const noDataEvent = new DemographicsUserBasedNoDataEvent();
    noDataEvent.reset("gender");
    noDataEvent.reset("age");
    const fetchService = DefaultFetchService.getInstance();
    const queryParams = getQueryParams();
    const rawDataPromise = fetchService.get<IRawData>(ENDPOINT, queryParams);
    return new Promise((resolve, reject) => {
        rawDataPromise
            .then((rawDataAPI) => {
                const domains = queryParams.keys.split(DEFAULT_WEBSITES_DELIMITER);
                const rawData = domains.reduce((acc, domain) => {
                    return { ...acc, [domain]: rawDataAPI[domain] ?? EMPTY_VALUE };
                }, {});

                if (isNoData(rawData)) {
                    noDataEvent.noData("gender");
                    noDataEvent.noData("age");
                }

                resolve(rawData);
            })
            .catch(reject);
    });
};

export const getExcelLink = () => {
    const queryParams = getQueryParams();
    const fetcherService = DefaultFetchService.getInstance();
    const excelLink = `${EXCEL_ENDPOINT}?${fetcherService.requestParams(queryParams)}`;
    return excelLink;
};
